/** Parse YYYY-MM-DD or ISO date prefix into local calendar Date (no UTC shift for the day). */
export function dateFromYmdParts(iso) {
  if (!iso) return null;
  const part = String(iso).split('T')[0];
  const [y, m, d] = part.split('-').map((n) => parseInt(n, 10));
  if (!y || !m || !d) return null;
  const dt = new Date(y, m - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d) return null;
  return dt;
}

/** Format a calendar date (YYYY-MM-DD or ISO prefix) as MM/DD/YYYY. */
export function formatUsDateFromIso(iso) {
  const dt = dateFromYmdParts(iso);
  if (!dt) return '';
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  const yyyy = dt.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
}

/**
 * Parse API datetime strings for display in the user's local timezone.
 * Naive ISO datetimes from the backend (no Z / offset) are treated as UTC,
 * matching server storage and get_time_ago on the API.
 */
export function parseApiDateTime(isoOrStr) {
  if (!isoOrStr) return null;
  const s = String(isoOrStr).trim();
  if (!s) return null;

  if (/[zZ]$/.test(s) || /[+-]\d{2}:\d{2}$/.test(s)) {
    const dt = new Date(s);
    return isNaN(dt.getTime()) ? null : dt;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    return dateFromYmdParts(s);
  }

  if (s.includes('T')) {
    const dt = new Date(`${s}Z`);
    return isNaN(dt.getTime()) ? null : dt;
  }

  const dt = new Date(s);
  return isNaN(dt.getTime()) ? null : dt;
}

/** Format an API datetime string as MM/DD/YYYY in local time. */
export function formatUsDateFromDateTime(isoOrStr) {
  const dt = parseApiDateTime(isoOrStr);
  if (!dt) return '';
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  const yyyy = dt.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
}

/** Format an API datetime string as HH:MM (24h) in local time. */
export function formatUsTimeFromDateTime(isoOrStr) {
  const dt = parseApiDateTime(isoOrStr);
  if (!dt) return '';
  return `${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`;
}

/** Format an API datetime string as MM/DD/YYYY HH:MM (24h) in local time. */
export function formatUsDateTimeFromIso(isoOrStr) {
  const dt = parseApiDateTime(isoOrStr);
  if (!dt) return '';
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  const yyyy = dt.getFullYear();
  const hh = String(dt.getHours()).padStart(2, '0');
  const min = String(dt.getMinutes()).padStart(2, '0');
  return `${mm}/${dd}/${yyyy} ${hh}:${min}`;
}

/** Today's calendar date in local time as YYYY-MM-DD. */
export function todayIsoLocal() {
  const t = new Date();
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
}

/** Parse MM/DD/YYYY (or M/D/YYYY) to YYYY-MM-DD, or null if invalid. */
export function parseUsDateToIso(str) {
  const trimmed = String(str).trim();
  const m = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return null;
  const month = parseInt(m[1], 10);
  const day = parseInt(m[2], 10);
  const year = parseInt(m[3], 10);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const dt = new Date(year, month - 1, day);
  if (dt.getFullYear() !== year || dt.getMonth() !== month - 1 || dt.getDate() !== day) return null;
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** Normalize numeric typing toward MM/DD/YYYY with slashes. */
export function normalizeUsDateTyping(raw) {
  const digits = String(raw).replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

/** Resolve YYYY-MM-DD from MM/DD/YYYY text or fall back to stored ISO date. */
export function resolveIsoFromUsForm(displayStr, storedIso) {
  const parsed = parseUsDateToIso(String(displayStr).trim());
  if (parsed) return parsed;
  if (storedIso && /^\d{4}-\d{2}-\d{2}$/.test(storedIso)) return storedIso;
  return null;
}

/** Inclusive calendar-day span between two YYYY-MM-DD strings (UTC calendar math, DST-safe). */
export function inclusiveDaySpanYmd(startYmd, endYmd) {
  const [ys, ms, ds] = String(startYmd).split('T')[0].split('-').map((n) => parseInt(n, 10));
  const [ye, me, de] = String(endYmd).split('T')[0].split('-').map((n) => parseInt(n, 10));
  if (!ys || !ms || !ds || !ye || !me || !de) return 0;
  const a = Date.UTC(ys, ms - 1, ds);
  const b = Date.UTC(ye, me - 1, de);
  return Math.round((b - a) / 86400000) + 1;
}

/**
 * Inclusive business-day count between YYYY-MM-DD strings.
 * Matches backend calculate_business_days(exclude_fridays=True): Fridays excluded; weekends counted.
 */
export function calculateBusinessDaysYmd(startYmd, endYmd, excludeFridays = true) {
  const start = dateFromYmdParts(startYmd);
  const end = dateFromYmdParts(endYmd);
  if (!start || !end) return 0;
  if (start > end) return 0;

  let businessDays = 0;
  const current = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const endLocal = new Date(end.getFullYear(), end.getMonth(), end.getDate());

  while (current <= endLocal) {
    if (!(excludeFridays && current.getDay() === 5)) {
      businessDays += 1;
    }
    current.setDate(current.getDate() + 1);
  }

  return businessDays;
}

/** Add signed whole calendar days to a YYYY-MM-DD string; returns YYYY-MM-DD or ''. */
export function addDaysToYmd(ymd, deltaDays) {
  const part = String(ymd).split('T')[0];
  const [y, m, d] = part.split('-').map((n) => parseInt(n, 10));
  if (!y || !m || !d) return '';
  const t = new Date(Date.UTC(y, m - 1, d + deltaDays));
  return t.toISOString().slice(0, 10);
}

/** Format a local Date as YYYY-MM-DD. */
export function toYmdLocal(dt) {
  if (!dt || isNaN(dt.getTime())) return '';
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}

/** Sunday–Saturday week containing anchor YYYY-MM-DD. */
export function weekRangeYmd(anchorYmd) {
  const dt = dateFromYmdParts(anchorYmd);
  if (!dt) return null;
  const sundayOffset = -dt.getDay();
  const start = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate() + sundayOffset);
  const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6);
  return { start: toYmdLocal(start), end: toYmdLocal(end) };
}

/** First and last calendar day of the month for YYYY-MM or YYYY-MM-DD anchor. */
export function monthRangeYmd(anchorYmdOrYm) {
  const part = String(anchorYmdOrYm).split('T')[0];
  const bits = part.split('-').map((n) => parseInt(n, 10));
  const y = bits[0];
  const m = bits[1];
  if (!y || !m || m < 1 || m > 12) return null;
  const start = new Date(y, m - 1, 1);
  const end = new Date(y, m, 0);
  return { start: toYmdLocal(start), end: toYmdLocal(end) };
}

/** Human-readable label for day / week / month attendance filters. */
export function attendancePeriodLabel(period, anchorYmd, monthYm) {
  if (period === 'week') {
    const r = weekRangeYmd(anchorYmd);
    if (!r) return '';
    return `${formatUsDateFromIso(r.start)} – ${formatUsDateFromIso(r.end)}`;
  }
  if (period === 'month') {
    const ym = monthYm || (anchorYmd ? anchorYmd.slice(0, 7) : '');
    const r = monthRangeYmd(ym ? `${ym}-01` : '');
    if (!r) return '';
    return `${formatUsDateFromIso(r.start)} – ${formatUsDateFromIso(r.end)}`;
  }
  return formatUsDateFromIso(anchorYmd) || anchorYmd || '';
}
